import {
  P2GBox,
  P2GGrid,
  P2GLink,
  P2GText,
  P2GButton,
  P2GCheckBoxLegacy,
  P2GChip,
  P2GLinkGroup
} from '@parcel2go/core-ui';
import { useContext } from 'react';
import BrSplitter from '../../../../lib/helpers/BrSplitter';
import { GetLinkLabelIcon, GetLinkLabelIconSrc } from './GetLinkLabelIcon';
import { LinkModel } from './LinkModel';
import { Download, DownloadContext } from '../../../../lib/context/DownloadContext';
import { uid } from '../../../../lib/helpers/uid';
import { LinksProps } from './LinksProps';
import { useLinkTitle } from './useLinkTitle';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';

export const LinksInner: React.FC<LinksProps> = (props) => {
  const dctx = useContext(DownloadContext);
  const { title, description } = useLinkTitle();

  const onClickTrigger = (l: LinkModel) => {
    if (l.OnClick) {
      l.OnClick();
    }

    if (l.Async === true) {
      setTimeout(() => addDownload(l), 250);
    }
  };

  const addDownload = (l: LinkModel) => {
    dctx.dispatch({
      type: 'ADD',
      payload: {
        Href: l.Href,
        Label: `${props.tag} \n ${title(l)} `,
        Status: 'Processing',
        Type: l.Type,
        UID: uid()
      }
    });
  };

  const onClick = (l: LinkModel) => {
    if (l.OnClick) {
      l.OnClick();
    }
  };

  const getDl = (l: LinkModel) => {
    return (
      dctx.state.find((x) => x.Href === l.Href) || {
        Label: title(l),
        Href: undefined,
        Status: '',
        Type: 'Link'
      }
    );
  };

  const Linkies = (l: LinkModel, dl: Download, p: string) => {
    if (l.Async) {
      return dl?.Status === 'Processing' ? (
        <P2GLink>
          <BrSplitter label={p} />
        </P2GLink>
      ) : dl?.Status === 'Ready' ? (
        <P2GLink target="_blank" href={dl.Href}>
          <BrSplitter label={title(l)} />
        </P2GLink>
      ) : (
        <P2GLink onClick={() => onClickTrigger(l)}>
          <BrSplitter label={title(l)} />
        </P2GLink>
      );
    } else {
      return l.Disabled ? (
        <P2GLink key={l.Type} target={l.Target}>
          <span style={{ opacity: 0.5 }}>
            <BrSplitter label={title(l)} />
          </span>
        </P2GLink>
      ) : (
        <P2GLink key={l.Type} onClick={() => onClick(l)} href={l.Href} target={l.Target}>
          <BrSplitter label={title(l)} />
        </P2GLink>
      );
    }
  };

  if (props.variant === 'link') {
    return <P2GLinkGroup links={props.links.map((l) => Linkies(l, getDl(l), `Processing...`))} />;
  }

  if (props.variant === 'button') {
    return (
      <P2GGrid container spacing={1}>
        {props.links.map((l) => {
          if (l.Async) {
            const dl = dctx.state.find((x) => x.Href === l.Href) || {
              Label: title(l),
              Href: undefined,
              Status: '',
              Type: 'Button'
            };
            return (
              <P2GGrid item key={l.Type}>
                {dl?.Status === 'Processing' ? (
                  <P2GButton variant="primary">Processing...</P2GButton>
                ) : dl?.Status === 'Ready' ? (
                  <P2GButton variant="primary" onClick={() => onClick(l)} href={dl.Href}>
                    {title(l)}
                  </P2GButton>
                ) : (
                  <P2GButton variant="primary" onClick={() => onClickTrigger(l)}>
                    {title(l)}
                  </P2GButton>
                )}
              </P2GGrid>
            );
          } else {
            return (
              <P2GGrid item key={l.Type}>
                <P2GButton variant="primary" onClick={() => onClick(l)} href={l.Href}>
                  {title(l)}
                </P2GButton>
              </P2GGrid>
            );
          }
        })}
      </P2GGrid>
    );
  }

  if (props.variant === 'list') {
    return (
      <P2GBox>
        {props.links.map((l, i) => {
          const dl = getDl(l);
          return (
            <P2GBox key={l.Type} mt={i > 0 ? 1 : 0}>
              <P2GGrid container alignItems="center" gap={2} wrap="nowrap">
                {!l.HideIcon && (
                  <P2GGrid item flexShrink={1}>
                    <CdnIconProxy
                      size="sm"
                      palette={l.Checked === true ? 'success' : 'primary'}
                      src={GetLinkLabelIconSrc(l.Type)}
                    >
                      {GetLinkLabelIcon(l.Type)}
                    </CdnIconProxy>
                  </P2GGrid>
                )}
                <P2GGrid item flexGrow={1}>
                  <P2GGrid container wrap="nowrap" gap={2}>
                    <P2GGrid item flexGrow={1}>
                      <P2GText variant="body2">{description(l)}</P2GText>
                      {l.AdditionalText && (
                        <P2GText component="p" variant="body2">
                          <strong>{l.AdditionalText}</strong>
                        </P2GText>
                      )}
                    </P2GGrid>
                    {dl?.Status === 'Ready' && (
                      <P2GGrid item flexShrink={1}>
                        <P2GChip color="primary" label="Ready" size="small" />
                      </P2GGrid>
                    )}
                    <P2GGrid item xs={4}>
                      <P2GBox textAlign="right">
                        {l.Checked !== undefined ? (
                          <P2GCheckBoxLegacy
                            checked={l.Checked}
                            onChange={() => onClick(l)}
                            disabled={l.Disabled}
                            labelPlacement="end"
                            variant="primary"
                            error={false}
                            id="link"
                          />
                        ) : (
                          l.Control || Linkies(l, dl, `Processing...`)
                        )}
                      </P2GBox>
                    </P2GGrid>
                  </P2GGrid>
                </P2GGrid>
              </P2GGrid>
            </P2GBox>
          );
        })}
      </P2GBox>
    );
  }

  return null;
};
